// PRM SLA KPI Report — fixed scoring rules and period engine.
//
// Source: "Dublin 2026 PRM SLA.xlsx", Aug-26 tab (structure unchanged since
// Jan-26), with the scoring methodology DAA confirmed on 30 Sep 2026:
//
//   daily actual % → period average (each applicable day weighted equally,
//   NOT pooled pax) → round to a whole % (.50 rounds up) → compare with the
//   stated target → all-or-nothing marks for that KPI.
//
//   A day with no applicable passengers is shown as "N/A – No applicable
//   passengers" and treated as a pass. A KPI with no applicable passengers on
//   any day of the range gets full marks and stays in the 50-mark denominator.
//
// These rules are contractual and deliberately NOT user editable. Minutes and
// targets are fixed here rather than read off the payload; the engine flags a
// mismatch if the backend ever scores against something different.

export const MAX_MARKS = 50;

// How an N/A day enters the period average. DAA confirmed N/A = pass but not
// whether it is left out of the average (the workbook's AVERAGE() behaviour)
// or counted as 100% — follow-up sent 30 Sep 2026. Change here once answered.
//   'exclude' — average only the days with applicable passengers
//   'as100'   — an N/A day counts as 100% in the average
export const NA_DAY_AVERAGE_MODE = 'exclude';

export const NA_LABEL = 'N/A – No applicable passengers';

export const SLA_KPI_SECTIONS = [
    {
        key: 'externalPoint',
        title: 'KPI – External Point',
        summaryKey: 'externalCallPoint',
        measurements: [
            { kpiKey: 'preAdvisedA', label: 'Pre-Advised', minutes: 10, target: 98, maxMarks: 1 },
            { kpiKey: 'preAdvisedB', label: 'Pre-Advised', minutes: 20, target: 100, maxMarks: 1 },
            { kpiKey: 'nonAdvisedA', label: 'Not Pre-Advised', minutes: 20, target: 98, maxMarks: 1 },
        ],
    },
    {
        key: 'terminalReception',
        title: 'KPI – Terminal Reception',
        summaryKey: 'ecac',
        measurements: [
            { kpiKey: 'preAdvisedA', label: 'Pre-Advised', minutes: 15, target: 95, maxMarks: 2 },
            { kpiKey: 'preAdvisedB', label: 'Pre-Advised', minutes: 20, target: 100, maxMarks: 2 },
            { kpiKey: 'nonAdvisedA', label: 'Not Pre-Advised', minutes: 20, target: 95, maxMarks: 2 },
            { kpiKey: 'nonAdvisedB', label: 'Not Pre-Advised', minutes: 30, target: 100, maxMarks: 2 },
        ],
    },
    {
        key: 'boardings',
        title: 'KPI – Outbound Boardings',
        summaryKey: 'boardingSla',
        // boarding minutes are BEFORE departure, shown as -35 in the workbook
        minutesBefore: true,
        measurements: [
            { kpiKey: 'lt230SeatsA', label: 'Less than 230 seats', minutes: 35, target: 93, maxMarks: 2.5 },
            { kpiKey: 'lt230SeatsB', label: 'Less than 230 seats', minutes: 30, target: 100, maxMarks: 2.5 },
            { kpiKey: 'gte230SeatsA', label: 'Greater than or equal to 230 seats', minutes: 50, target: 93, maxMarks: 2.5 },
            { kpiKey: 'gte230SeatsB', label: 'Greater than or equal to 230 seats', minutes: 45, target: 100, maxMarks: 2.5 },
        ],
    },
    {
        key: 'firstWave',
        title: 'KPI – EI/FR Outbound Boardings',
        summaryKey: 'boardingSla',
        minutesBefore: true,
        measurements: [
            { kpiKey: 'firstWave', label: 'Less than 230 seats – First Wave', minutes: 35, target: 98, maxMarks: 5 },
        ],
    },
    {
        key: 'inbounds',
        title: 'KPI – Inbounds',
        summaryKey: 'inboundSla',
        measurements: [
            { kpiKey: 'lt230SeatsA', label: 'Less than 230 seats', minutes: 6, target: 93, maxMarks: 2 },
            { kpiKey: 'lt230SeatsB', label: 'Less than 230 seats', minutes: 11, target: 100, maxMarks: 2 },
            { kpiKey: 'lt230SeatsC', label: 'Less than 230 seats – all disembarked', minutes: 21, target: 100, maxMarks: 2 },
            { kpiKey: 'gte230SeatsA', label: 'Greater than or equal to 230 seats', minutes: 16, target: 93, maxMarks: 2 },
            { kpiKey: 'gte230SeatsB', label: 'Greater than or equal to 230 seats', minutes: 21, target: 100, maxMarks: 2 },
            { kpiKey: 'gte230SeatsC', label: 'Greater than or equal to 230 seats – all disembarked', minutes: 31, target: 100, maxMarks: 2 },
        ],
    },
    {
        key: 'holdingPoint',
        title: 'KPI – Terminal Holding Point 218/407',
        summaryKey: 'terminalReception',
        measurements: [
            { kpiKey: 'preAdvisedA', label: 'Pre-Advised', minutes: 10, target: 93, maxMarks: 2.5 },
            { kpiKey: 'preAdvisedB', label: 'Pre-Advised', minutes: 15, target: 100, maxMarks: 2.5 },
            { kpiKey: 'nonAdvisedA', label: 'Not Pre-Advised', minutes: 15, target: 93, maxMarks: 2.5 },
            { kpiKey: 'nonAdvisedB', label: 'Not Pre-Advised', minutes: 20, target: 100, maxMarks: 2.5 },
        ],
    },
    {
        key: 'transfers',
        title: 'KPI – Transfer Passengers',
        summaryKey: 'transferSla',
        measurements: [
            {
                kpiKey: 'preAdvised',
                label: 'Pre-Advised – all met and achieved all above KPIs or within the min airline connect time',
                criteria: 'Onwards flight made',
                minutes: null,
                target: 100,
                maxMarks: 2,
            },
        ],
    },
];

// Round half up to a whole percent. Snap to 2dp first so float noise such as
// 97.49999999 cannot turn a genuine 97.50 into 97.
export function roundPercent(value) {
    const snapped = Math.round(value * 100) / 100;
    return Math.floor(snapped + 0.5);
}

export function measurementCriteria(section, m) {
    if (m.criteria) return m.criteria;
    if (m.minutes == null) return '';
    return section.minutesBefore ? `${m.minutes} min before departure` : `${m.minutes} min`;
}

function parseTarget(raw) {
    if (raw == null) return null;
    const num = parseFloat(String(raw).replace('%', ''));
    return Number.isFinite(num) ? num : null;
}

// One KPI on one day. `total` is the post-skip applicable pax count (see
// recomputeKpiExcludingSkips in prm-cpu), so total 0 means nobody to measure.
function evaluateDay(kpi, target) {
    const total = kpi?.total ?? kpi?.totalAssisted ?? 0;
    if (!kpi || !total || kpi.score == null) {
        return { applicable: false, actual: null, pax: 0, display: NA_LABEL, status: 'pass', statusLabel: 'Pass (N/A)' };
    }
    const actual = Number(kpi.score);
    const met = roundPercent(actual) >= target;
    return {
        applicable: true,
        actual,
        pax: total,
        display: `${actual}%`,
        status: met ? 'pass' : 'fail',
        statusLabel: met ? 'Pass' : 'Fail',
    };
}

// Backend minutes/target for a KPI, taken from any day that carries it, so the
// report can flag if the fixed rules above ever drift from what was scored.
function detectDrift(m, days) {
    const sample = days.find((d) => d.kpi && (d.kpi.minutes != null || d.kpi.target != null))?.kpi;
    if (!sample) return null;
    const issues = [];
    if (m.minutes != null && sample.minutes != null && Number(sample.minutes) !== m.minutes) {
        issues.push(`backend minutes ${sample.minutes}, report rule ${m.minutes}`);
    }
    const backendTarget = parseTarget(sample.target);
    if (backendTarget != null && backendTarget !== m.target) {
        issues.push(`backend target ${backendTarget}%, report rule ${m.target}%`);
    }
    return issues.length ? issues.join('; ') : null;
}

// All calendar dates from start to end inclusive, as 'YYYY-MM-DD'. Built in
// UTC so a DST change inside the range cannot skip or repeat a day.
export function enumerateDates(startDate, endDate) {
    const parse = (s) => {
        const [y, mo, d] = String(s).split('-').map(Number);
        return Date.UTC(y, mo - 1, d);
    };
    const dates = [];
    for (let t = parse(startDate); t <= parse(endDate); t += 86400000) {
        const dt = new Date(t);
        dates.push(`${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`);
    }
    return dates;
}

// Period result for one KPI measurement from its daily results.
export function evaluateMeasurement(m, dailyKpis, { naMode = NA_DAY_AVERAGE_MODE } = {}) {
    const days = dailyKpis.map(({ date, kpi }) => ({ date, kpi, ...evaluateDay(kpi, m.target) }));
    const applicable = days.filter((d) => d.applicable);
    const naDays = days.length - applicable.length;

    let periodAverage = null;
    if (applicable.length) {
        const values = naMode === 'as100'
            ? days.map((d) => (d.applicable ? d.actual : 100))
            : applicable.map((d) => d.actual);
        periodAverage = values.reduce((sum, v) => sum + v, 0) / values.length;
    }

    // no applicable passengers on any day → pass with full marks (DAA, 30 Sep)
    const roundedResult = periodAverage == null ? null : roundPercent(periodAverage);
    const met = roundedResult == null ? true : roundedResult >= m.target;

    return {
        ...m,
        days,
        totalDays: days.length,
        applicableDays: applicable.length,
        naDays,
        daysPassed: days.filter((d) => d.status === 'pass').length,
        daysFailed: days.filter((d) => d.status === 'fail').length,
        periodAverage,
        roundedResult,
        met,
        allNa: applicable.length === 0,
        marks: met ? m.maxMarks : 0,
        drift: detectDrift(m, days),
    };
}

// Whole report. `daily` maps summaryKey → dailyReports array; `errors` maps
// summaryKey → message for sub-reports that failed to load. A failed
// sub-report is never scored as N/A-pass — its KPIs are marked unavailable
// and earn nothing, so a fetch failure cannot inflate the score.
export function buildSlaKpiReport({ daily, errors = {}, startDate, endDate, naMode = NA_DAY_AVERAGE_MODE }) {
    const dates = enumerateDates(startDate, endDate);

    const byDate = {};
    Object.keys(daily || {}).forEach((key) => {
        const map = {};
        (daily[key] || []).forEach((report) => {
            if (report?.date) map[String(report.date).slice(0, 10)] = report;
        });
        byDate[key] = map;
    });

    const sections = SLA_KPI_SECTIONS.map((section) => {
        const unavailable = errors[section.summaryKey] || null;
        const measurements = section.measurements.map((m) => {
            if (unavailable) {
                return {
                    ...m,
                    unavailable,
                    days: dates.map((date) => ({ date, applicable: false, display: 'Data unavailable', status: 'unavailable', statusLabel: '—' })),
                    totalDays: dates.length,
                    applicableDays: 0,
                    naDays: 0,
                    daysPassed: 0,
                    daysFailed: 0,
                    periodAverage: null,
                    roundedResult: null,
                    met: false,
                    allNa: false,
                    marks: 0,
                    drift: null,
                };
            }
            const dailyKpis = dates.map((date) => ({ date, kpi: byDate[section.summaryKey]?.[date]?.kpis?.[m.kpiKey] ?? null }));
            return evaluateMeasurement(m, dailyKpis, { naMode });
        });
        return {
            ...section,
            unavailable,
            measurements,
            maxMarks: measurements.reduce((s, m) => s + m.maxMarks, 0),
            marks: measurements.reduce((s, m) => s + m.marks, 0),
        };
    });

    return {
        dates,
        sections,
        maxMarks: sections.reduce((s, sec) => s + sec.maxMarks, 0),
        marks: sections.reduce((s, sec) => s + sec.marks, 0),
        hasUnavailable: sections.some((s) => s.unavailable),
        drift: sections.flatMap((s) => s.measurements.filter((m) => m.drift).map((m) => ({ section: s.title, label: m.label, drift: m.drift }))),
        naMode,
    };
}
